import { getAnalyticsPageMetadata } from './pageMetadata';

const MAX_LABEL_LENGTH = 64;
const EXCLUDED_SELECTOR = [
  '.mp-no-track', '.mp-sensitive', '[data-analytics-ignore="true"]',
  '[data-analytics-sensitive="true"]', 'input', 'textarea', 'select',
  '[contenteditable="true"]',
].join(',');
const EXCLUDED_TEXT_SELECTOR = `${EXCLUDED_SELECTOR},svg,img,script,style,[hidden],[aria-hidden="true"]`;
const ACRONYMS = new Set(['ai', 'api', 'otp', 'pdf', 'csv', 'id', 'url', 'bmi', 'bmr', 'tdee', 'dna', 'usda', 'faq']);

const humanize = (value: string): string => value
  .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
  .replace(/([a-z\d])([A-Z])/g, '$1 $2')
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .split(' ')
  .map((word) => ACRONYMS.has(word.toLowerCase())
    ? word.toUpperCase()
    : `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
  .join(' ');

// Reject identifying values instead of letting email addresses, phone numbers,
// record IDs, or paragraphs create a different event for every record.
const conciseLabel = (value: string | null | undefined): string | undefined => {
  if (/@|https?:\/\/|\b[0-9a-f]{8}-[0-9a-f-]{27,}\b|(?:\+?\d[\d\s().-]{7,}\d)/i.test(value || '')) return undefined;
  const label = humanize(value || '').replace(/\s*\(\d+\)$/, '').trim();
  if (!label || label.length > MAX_LABEL_LENGTH || label.split(' ').length > 10) return undefined;
  if (!/[a-z]/i.test(label)) return undefined;
  return label;
};

const readVisibleText = (element: Element): string => {
  const parts: string[] = [];
  let length = 0;
  const visit = (node: Node) => {
    if (length > MAX_LABEL_LENGTH * 2) return;
    if (node.nodeType === 3) {
      const text = node.textContent || '';
      parts.push(text);
      length += text.length;
      return;
    }
    if (node.nodeType !== 1 || (node as Element).matches(EXCLUDED_TEXT_SELECTOR)) return;
    node.childNodes.forEach(visit);
  };
  visit(element);
  return parts.join(' ').replace(/\s+/g, ' ').trim();
};

const ICON_ACTIONS: Record<string, string> = {
  'arrow-left': 'Go Back', 'arrow-right': 'Continue', 'arrow-up-right': 'Open Link',
  'chevron-left': 'Previous', 'chevron-right': 'Next',
  'chevron-down': 'Expand', 'chevron-up': 'Collapse',
  'chevrons-up-down': 'Open Selection', 'keyboard-arrow-down': 'Expand',
  x: 'Close', close: 'Close', check: 'Confirm', done: 'Confirm',
  plus: 'Add', add: 'Add', minus: 'Remove', remove: 'Remove',
  trash: 'Delete', 'trash-2': 'Delete', delete: 'Delete',
  pencil: 'Edit', 'square-pen': 'Edit', edit: 'Edit',
  'more-horizontal': 'More Actions', 'more-vertical': 'More Actions',
  ellipsis: 'More Actions', 'ellipsis-vertical': 'More Actions',
  'more-horiz': 'More Actions', 'more-vert': 'More Actions',
  search: 'Search', filter: 'Filter', 'list-filter': 'Filter',
  settings: 'Open Settings', menu: 'Open Menu',
  download: 'Download', upload: 'Upload', copy: 'Copy', save: 'Save',
  'log-out': 'Log Out', logout: 'Log Out',
  'external-link': 'Open Link', 'refresh-cw': 'Refresh', refresh: 'Refresh',
  'rotate-ccw': 'Reset', calendar: 'Open Calendar', 'calendar-days': 'Open Calendar',
  'calendar-month': 'Open Calendar', 'calendar-today': 'Open Calendar',
  eye: 'View Details', visibility: 'View Details',
  'eye-off': 'Hide Details', 'visibility-off': 'Hide Details',
  'sliders-horizontal': 'Open Filters', 'panel-left-close': 'Collapse Sidebar',
  'panel-left-open': 'Expand Sidebar', bell: 'Open Notifications',
  'message-circle': 'Open Messages', 'message-square': 'Open Messages',
  'user-round': 'Open User Menu', printer: 'Print', share: 'Share', 'share-2': 'Share',
};

const getIconAction = (element: HTMLElement): string | undefined => {
  for (const icon of element.querySelectorAll('svg')) {
    const lucideName = (icon.getAttribute('class') || '').split(/\s+/)
      .find((className) => className.startsWith('lucide-'))?.slice('lucide-'.length);
    if (lucideName && ICON_ACTIONS[lucideName]) return ICON_ACTIONS[lucideName];
    const muiName = (icon.getAttribute('data-testid') || '')
      .replace(/(?:Outlined|Outline|Rounded|Round|TwoTone|Sharp|Filled)*Icon$/, '')
      .replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    if (ICON_ACTIONS[muiName]) return ICON_ACTIONS[muiName];
  }
  return undefined;
};

const getLinkDestination = (element: HTMLElement): URL | undefined => {
  const href = element.closest('a[href]')?.getAttribute('href');
  if (!href) return undefined;
  try {
    return new URL(href, element.ownerDocument?.location?.href || 'https://analytics.local/');
  } catch {
    return undefined;
  }
};

/** A concise action label, never input values or a full card's text content. */
export const getAnalyticsElementLabel = (element: HTMLElement): string => {
  if (element.closest(EXCLUDED_SELECTOR)) return 'Private Control';

  const explicit = conciseLabel(element.dataset.analyticsLabel);
  if (explicit) return explicit;

  const destination = getLinkDestination(element);
  const currentOrigin = element.ownerDocument?.location?.origin || 'https://analytics.local';
  const isInternal = destination?.origin === currentOrigin;
  // Client names are often the only visible text inside these links, including demo links.
  if (isInternal && /^\/(?:demo\/)?clients\/(?!add(?:\/|$))[^/]+(?:\/|$)/.test(destination.pathname)) {
    return 'Open Client Details';
  }

  const role = element.getAttribute('role');
  if (role === 'option' || element.closest('[role="option"]')) return 'Select Option';
  if (role === 'combobox' || element.getAttribute('aria-haspopup') === 'listbox') return 'Open Selection';

  const accessible = conciseLabel(element.getAttribute('aria-label')) || conciseLabel(element.getAttribute('title'));
  if (accessible) return accessible;

  if (element.getAttribute('aria-haspopup') === 'menu') return 'Open Menu';
  if (element.closest('[role="gridcell"]') && /^\d{1,2}$/.test(readVisibleText(element))) return 'Select Date';

  if (isInternal && destination && !destination.hash) {
    const page = getAnalyticsPageMetadata(destination.pathname, destination.search);
    if (page.pageRoute !== '*') return `Open ${page.pageName}`;
  }
  if (destination?.protocol === 'mailto:') return 'Send Email';
  if (destination?.protocol === 'tel:') return 'Call Phone Number';

  const text = conciseLabel(readVisibleText(element));
  if (text) return text;

  const icon = getIconAction(element);
  if (icon) return icon;
  if (role === 'switch' || role === 'checkbox') return 'Toggle Selection';
  if (role === 'radio') return 'Select Option';
  if (role === 'tab') return 'Open Tab';
  if (element.getAttribute('aria-expanded') !== null) return 'Toggle Details';
  if (element.getAttribute('type') === 'submit') return 'Submit Form';
  if (destination || role === 'link') return 'Open Link';
  return 'Open Control';
};

export const getAnalyticsElementSection = (element: HTMLElement): string => {
  if (element.closest(EXCLUDED_SELECTOR)) return 'Private Section';
  const explicit = conciseLabel(element.closest<HTMLElement>('[data-analytics-section]')?.dataset.analyticsSection);
  if (explicit) return explicit;

  const region = element.closest('section[aria-label],nav[aria-label],aside[aria-label],form[aria-label]');
  const label = conciseLabel(region?.getAttribute('aria-label'));
  if (label) return label;

  const landmark = element.closest('nav,header,aside,main,footer,form,section');
  const names: Record<string, string> = {
    nav: 'Navigation', header: 'Header', aside: 'Sidebar', main: 'Main Content',
    footer: 'Footer', form: 'Form', section: 'Page Section',
  };
  return landmark ? names[landmark.tagName.toLowerCase()] : 'Page Content';
};

export const getAnalyticsClickEventName = (pageName: string, label: string): string => {
  const action = (conciseLabel(label) || 'Open Control').replace(/\s+Clicked$/i, '');
  return `${pageName}: ${action} Clicked`;
};
